import { ReactNode, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, isPowerUser, hasAnyPdpoRole, getPdpoRoleName } from '../../contexts/AuthContext';
import { useCredits } from '../../hooks/useCredits';
import {
  Coins,
  Building2,
  LogOut,
  Sparkles,
  Bell,
  HelpCircle,
  Inbox,
  ListChecks,
  ShieldCheck,
  FileText,
  GraduationCap,
  Users,
  Shield,
  CreditCard,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from 'lucide-react';
import { getNotificationCounts, subscribeToNotifications } from '../../services/notificationService';

// ---------------------------------------------------------------------------
// Nav definition
// ---------------------------------------------------------------------------

interface NavLink {
  path: string;
  label: string;
  icon?: ReactNode;
}

type SubgroupAccent = 'red' | 'amber' | 'blue' | 'emerald';

interface NavGroup {
  id: string;
  label: string;
  defaultOpen: boolean;
  items?: NavLink[];
  // For nested sub-groups (used inside WORKFLOWS)
  subgroups?: { id: string; label: string; accent: SubgroupAccent; items: NavLink[] }[];
}

const SUBGROUP_STYLES: Record<SubgroupAccent, { header: string; border: string }> = {
  red: { header: 'text-red-700', border: 'border-red-300' },
  amber: { header: 'text-amber-700', border: 'border-amber-300' },
  blue: { header: 'text-blue-700', border: 'border-blue-300' },
  emerald: { header: 'text-emerald-700', border: 'border-emerald-300' },
};

interface DashboardLayoutNewProps {
  children: ReactNode;
}

export function DashboardLayoutNew({ children }: DashboardLayoutNewProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, signOut } = useAuth();
  const { balance, loading: creditsLoading } = useCredits();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const menuRef = useRef<HTMLDivElement>(null);

  const isPower = isPowerUser(userProfile);
  const isAdminRole = hasAnyPdpoRole(userProfile, ['DPO', 'CEO', 'CFO']);
  const roleName = getPdpoRoleName(userProfile);

  // ---- click-outside / esc for the user dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowUserMenu(false);
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showUserMenu]);

  // ---- notifications
  useEffect(() => {
    if (!userProfile?.id) return;
    void loadNotificationCount();
    const unsubscribe = subscribeToNotifications(userProfile.id, () => {
      void loadNotificationCount();
    });
    return () => unsubscribe();
  }, [userProfile?.id]);

  const loadNotificationCount = async () => {
    if (!userProfile?.id) return;
    try {
      const counts = await getNotificationCounts(userProfile.id);
      setNotificationCount(counts.unread);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  // ---- nav groups
  const groups = buildNavGroups({ isPower, isAdminRole });

  const isGroupOpen = (id: string, defaultOpen: boolean): boolean => {
    if (id in openGroups) return openGroups[id];
    return defaultOpen;
  };

  const toggleGroup = (id: string, defaultOpen: boolean) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !(id in prev ? prev[id] : defaultOpen) }));
  };

  const handleSignOut = async () => {
    try {
      setShowUserMenu(false);
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleMenuNavigation = (path: string) => {
    setShowUserMenu(false);
    navigate(path);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ============================ HEADER ============================ */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-6">
              <button
                onClick={() => navigate(isPower ? '/dashboard' : '/actions')}
                className="hover:opacity-80 transition-opacity text-left"
              >
                <h1 className="text-xl font-bold text-slate-900">PDPO Assist</h1>
                <p className="text-xs text-slate-600">Compliance Platform</p>
              </button>

              {/* Digital DPO chat shortcut (renamed from DPO.ai) */}
              <button
                onClick={() => navigate('/ai-adviser')}
                className={`relative flex items-center space-x-2 px-5 py-2.5 rounded-lg font-semibold shadow-lg transition-all duration-300 overflow-hidden group ${
                  location.pathname === '/ai-adviser'
                    ? 'bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-600 text-white shadow-blue-500/50 scale-105'
                    : 'bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-500 text-white hover:shadow-xl hover:scale-105 shadow-blue-400/40'
                }`}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 translate-x-[-100%] group-hover:translate-x-[100%] transition-all duration-1000"></div>
                <Sparkles className="w-5 h-5 relative z-10" />
                <span className="relative z-10 font-bold">Digital DPO</span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 blur-xl transition-opacity"></div>
              </button>
            </div>

            <div className="flex items-center space-x-4">
              {/* Switch back to the classic interface */}
              <button
                onClick={() => navigate('/dashboard?ui=classic')}
                className="hidden md:flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 hover:text-slate-900 hover:border-slate-300 transition"
                title="Switch back to the classic interface"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span>Back to classic</span>
              </button>

              {isPower && (
                <button
                  onClick={() => navigate('/credits')}
                  className="flex items-center space-x-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition"
                  title="View credits and billing"
                >
                  <Coins className="w-5 h-5 text-slate-700" />
                  <div className="text-left">
                    <p className="text-xs text-slate-500 leading-none">Credits</p>
                    <p className="text-sm font-semibold text-slate-900 leading-tight">
                      {creditsLoading ? '...' : balance.toLocaleString()}
                    </p>
                  </div>
                </button>
              )}

              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{userProfile?.name}</p>
                <p className="text-xs text-slate-600">{roleName}</p>
              </div>

              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="relative w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold hover:bg-blue-200 transition-all hover:scale-105"
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  {userProfile?.name?.charAt(0).toUpperCase()}
                  {notificationCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {notificationCount > 9 ? '9+' : notificationCount}
                    </span>
                  )}
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-slate-200 py-2 animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                    <button
                      onClick={() => handleMenuNavigation('/profile')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                    >
                      <Building2 className="w-4 h-4 text-slate-500" />
                      <span className="font-medium">Company Profile</span>
                    </button>

                    <button
                      onClick={() => handleMenuNavigation('/notifications')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                    >
                      <Bell className="w-4 h-4 text-slate-500" />
                      <div className="flex items-center justify-between flex-1">
                        <span className="font-medium">Notifications</span>
                        {notificationCount > 0 && (
                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                            {notificationCount}
                          </span>
                        )}
                      </div>
                    </button>

                    <button
                      onClick={() => handleMenuNavigation('/help')}
                      className="w-full text-left px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center space-x-3"
                    >
                      <HelpCircle className="w-4 h-4 text-slate-500" />
                      <span className="font-medium">Help</span>
                    </button>

                    <div className="my-1 border-t border-slate-200"></div>

                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center space-x-3"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="font-medium">Sign Out</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ============================ MAIN ============================ */}
      <div className="flex">
        <aside className="w-64 bg-white border-r border-slate-200 min-h-[calc(100vh-4rem)] sticky top-16 overflow-y-auto max-h-[calc(100vh-4rem)]">
          <nav className="p-3 space-y-4">
            {groups.map((group) => (
              <NavGroupRender
                key={group.id}
                group={group}
                isOpen={isGroupOpen(group.id, group.defaultOpen)}
                onToggle={() => toggleGroup(group.id, group.defaultOpen)}
                currentPath={location.pathname}
                onNavigate={navigate}
              />
            ))}
          </nav>
        </aside>

        <main className="flex-1 p-6">{children}</main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NavGroup renderer
// ---------------------------------------------------------------------------

interface NavGroupRenderProps {
  group: NavGroup;
  isOpen: boolean;
  onToggle: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
}

function NavGroupRender({ group, isOpen, onToggle, currentPath, onNavigate }: NavGroupRenderProps) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-bold uppercase tracking-wider text-slate-800 hover:text-slate-900 border-b border-slate-200"
      >
        <span>{group.label}</span>
        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
      {isOpen && (
        <div className="mt-1 space-y-0.5">
          {group.items?.map((item) => (
            <NavItem key={item.path} item={item} currentPath={currentPath} onNavigate={onNavigate} />
          ))}
          {group.subgroups?.map((sub) => {
            const styles = SUBGROUP_STYLES[sub.accent];
            return (
              <div key={sub.id} className="mt-4 first:mt-2">
                <p
                  className={`px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${styles.header}`}
                >
                  {sub.label}
                </p>
                <div className={`ml-3 pl-2 border-l-2 ${styles.border} space-y-0.5`}>
                  {sub.items.map((item) => (
                    <NavItem key={item.path} item={item} currentPath={currentPath} onNavigate={onNavigate} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface NavItemProps {
  item: NavLink;
  currentPath: string;
  onNavigate: (path: string) => void;
}

function NavItem({ item, currentPath, onNavigate }: NavItemProps) {
  const isActive = currentPath === item.path;
  return (
    <button
      onClick={() => onNavigate(item.path)}
      className={`w-full flex items-center space-x-2 px-3 py-2 rounded-md text-sm transition text-left ${
        isActive ? 'bg-blue-50 text-blue-600 font-medium' : 'text-slate-700 hover:bg-slate-50'
      }`}
    >
      {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
      <span className="flex-1 truncate">{item.label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Group definitions
// ---------------------------------------------------------------------------

interface BuildArgs {
  isPower: boolean;
  isAdminRole: boolean;
}

function buildNavGroups({ isPower, isAdminRole }: BuildArgs): NavGroup[] {
  if (!isPower) {
    // Staff: flat, minimal
    return [
      {
        id: 'home',
        label: 'Home',
        defaultOpen: true,
        items: [
          { path: '/actions', label: 'My Inbox', icon: <Inbox className="w-4 h-4" /> },
          { path: '/training', label: 'Training', icon: <GraduationCap className="w-4 h-4" /> },
          { path: '/documents', label: 'Documents', icon: <FileText className="w-4 h-4" /> },
        ],
      },
    ];
  }

  const groups: NavGroup[] = [
    {
      id: 'home',
      label: 'Home',
      defaultOpen: true,
      items: [
        { path: '/dashboard', label: 'Inbox', icon: <Inbox className="w-4 h-4" /> },
        { path: '/actions', label: 'Action Tracker', icon: <ListChecks className="w-4 h-4" /> },
        { path: '/compliance', label: 'Programmes', icon: <ShieldCheck className="w-4 h-4" /> },
      ],
    },
    {
      id: 'workflows',
      label: 'Workflows',
      defaultOpen: true,
      subgroups: [
        {
          id: 'respond',
          label: 'Respond',
          accent: 'red',
          items: [
            { path: '/breaches', label: 'Report a breach' },
            { path: '/access-requests', label: 'Handle a request' },
          ],
        },
        {
          id: 'assess',
          label: 'Assess',
          accent: 'amber',
          items: [
            { path: '/data-register', label: 'Map an activity' },
            { path: '/dpia', label: 'Run a DPIA' },
            { path: '/cross-border', label: 'Assess a transfer' },
          ],
        },
        {
          id: 'document',
          label: 'Document',
          accent: 'blue',
          items: [
            { path: '/policies', label: 'Draft a policy or notice' },
            { path: '/retention', label: 'Set retention' },
            { path: '/documents-held', label: 'Destroy what you hold' },
            { path: '/dpmp', label: 'Issue a DPMP version' },
            { path: '/compliance/report/new', label: 'Generate a report' },
          ],
        },
        {
          id: 'enable',
          label: 'Enable',
          accent: 'emerald',
          items: [
            { path: '/training', label: 'Plan training' },
            { path: '/dpo-registry', label: 'Update DPO record' },
            { path: '/consent-forms', label: 'Build a consent form' },
            { path: '/consent', label: 'Manage consent' },
          ],
        },
      ],
    },
    {
      id: 'records',
      label: 'Records',
      defaultOpen: false,
      items: [
        { path: '/public-page', label: 'Public Trust Page' },
        { path: '/data-register', label: 'Data Register' },
        { path: '/consent-forms', label: 'Consent Forms' },
        { path: '/consent', label: 'Consent Records' },
        { path: '/access-requests', label: 'Access Requests' },
        { path: '/breaches', label: 'Breaches' },
        { path: '/dpia', label: 'DPIAs' },
        { path: '/cross-border', label: 'Cross-border Transfers' },
        { path: '/vendors', label: 'Vendors & Processors' },
        { path: '/policies', label: 'Policies & Notices' },
        { path: '/dpmp', label: 'DPMP Versions' },
        { path: '/retention', label: 'Retention Schedules' },
        { path: '/documents-held', label: 'Documents Held' },
        { path: '/training', label: 'Training' },
        { path: '/dpo-registry', label: 'DPO Registry' },
        { path: '/documents', label: 'Documents' },
        { path: '/evidence', label: 'Evidence Library' },
        { path: '/audit-log', label: 'Audit Log' },
      ],
    },
  ];

  if (isAdminRole) {
    groups.push({
      id: 'admin',
      label: 'Admin',
      defaultOpen: false,
      items: [
        { path: '/user-management', label: 'Users', icon: <Users className="w-4 h-4" /> },
        { path: '/authorities', label: 'PDPO Roles & Authorities', icon: <Shield className="w-4 h-4" /> },
        { path: '/credits', label: 'Credits & Billing', icon: <CreditCard className="w-4 h-4" /> },
      ],
    });
  }

  return groups;
}
